<script setup>
import { computed, watchEffect, ref } from "vue";
import { cva } from "class-variance-authority";
import { useI18n } from "vue-i18n";
import { FormInputType, InputSize } from "../../../../interfaces/input";
const props = defineProps({
  type: {
    type: String,
    default: FormInputType.TEXT
  },
  size: {
    type: String,
    default: InputSize.LARGE
  },
  placeholder: {
    type: String,
    default: ""
  },
  unit: {
    type: String,
    default: ""
  },
  isSelected: {
    type: Boolean,
    default: false
  },
  isDisabled: {
    type: Boolean,
    default: false
  },
  error: {
    type: String,
    default: ""
  },
  fullyRounded: {
    type: Boolean,
    default: false
  }
});
const { n } = useI18n();
const value = defineModel({ type: [String, Number], ...{
  required: true
} });
const inputContainerStyles = computed(
  () => cva("flex justify-between items-center border", {
    variants: {
      size: {
        small: "h-7 gap-1 px-1.5 py-[7.5px]",
        medium: "h-9 gap-1.5 p-2",
        large: "h-11 gap-2 p-3"
      },
      isSelected: {
        true: "border-light-blue-primary bg-primary-light",
        false: "bg-white"
      },
      isError: {
        true: "border-form-error"
      },
      isDisabled: {
        true: "border-gray-disabled"
      }
    }
  })({
    size: props.size,
    isSelected: props.isSelected && !props.isDisabled && props.error === "",
    isDisabled: props.isDisabled,
    isError: props.error !== "" && !props.isDisabled
  })
);
const borderRadiusClasses = computed(() => {
  if (props.fullyRounded) {
    return "rounded-full";
  }
  switch (props.size) {
    case InputSize.SMALL:
      return "rounded";
    case InputSize.MEDIUM:
      return "rounded-md";
    default:
      return "rounded-lg";
  }
});
const inputStyles = computed(
  () => cva("font-normal focus:outline-none w-full", {
    variants: {
      size: {
        small: "text-[11px] leading-[13px]",
        medium: "text-[13px] leading-4",
        large: "text-[15px] leading-[18px]"
      },
      isDefault: {
        true: "text-gray-l-800 placeholder:text-zinc-500"
      },
      isSelected: {
        true: "text-primary-info bg-primary-light placeholder:text-primary-info"
      },
      isError: {
        true: "text-badge-negative placeholder:text-badge-negative bg-inherit"
      },
      isDisabled: {
        true: "text-zinc-300 bg-white placeholder:text-zinc-300"
      }
    }
  })({
    size: props.size,
    isDefault: !props.isDisabled && !props.isSelected && props.error === "",
    isSelected: props.isSelected && !props.isDisabled && props.error === "",
    isDisabled: props.isDisabled,
    isError: props.error !== "" && !props.isDisabled
  })
);
const iconSize = computed(() => {
  switch (props.size) {
    case InputSize.SMALL:
      return "size-3";
    case InputSize.MEDIUM:
      return "size-4";
    default:
      return "size-5";
  }
});
const formattedValue = ref(value.value);
const updateValue = (event) => {
  const target = event.target;
  const numericValue = target.value.replace(/[^\d.,]/g, "").replace(",", ".");
  if (props.type === FormInputType.NUMBER) {
    if (!isNaN(Number(numericValue))) {
      value.value = Number(numericValue);
    } else {
      value.value = "";
    }
  } else {
    value.value = target.value;
  }
};
watchEffect(() => {
  if (props.type === FormInputType.NUMBER) {
    formattedValue.value = isNaN(Number(value.value)) ? "" : n(Number(value.value), {
      minimumFractionDigits: 0,
      maximumFractionDigits: 4
    });
  } else {
    formattedValue.value = value.value;
  }
});
const onNumberKeyPress = (event) => {
  if (props.type !== FormInputType.NUMBER) return;
  const allowed = /[0-9.,]/;
  if (!allowed.test(event.key)) {
    event.preventDefault();
  }
};
</script>

<template>
    <div class="flex flex-col gap-1">
        <div :class="[inputContainerStyles, borderRadiusClasses]">
            <div
                v-if="$slots.leftIcon"
                :class="iconSize"
                class="flex items-center"
            >
                <slot name="leftIcon" />
            </div>
            <input
                :value="formattedValue"
                :type="type === FormInputType.NUMBER ? 'text' : type"
                :placeholder
                :disabled="isDisabled"
                :class="[
  inputStyles,
  type === FormInputType.NUMBER && 'text-right'
]"
                @keypress="onNumberKeyPress"
                @input="updateValue"
            />
            <div
                v-if="unit || error || $slots.rightIcon"
                class="flex items-center justify-center gap-2"
            >
                <span v-if="unit" class="text-zinc-800">
                    {{ unit }}
                </span>
                <div
                    v-if="error || $slots.rightIcon"
                    class="flex items-center"
                    :class="iconSize"
                >
                    <IconExclamationCircle
                        v-if="error"
                        class="text-red-500"
                        is-solid
                    />
                    <slot v-else name="rightIcon" />
                </div>
            </div>
        </div>
        <span
            v-if="error"
            class="font-normal leading-4 text-icon-secondary-negative ml-4"
            :class="size === InputSize.SMALL ? 'text-[11px]' : 'text-[13px]'"
        >
            {{ error }}
        </span>
    </div>
</template>

<style scoped>
input[type=time]::-webkit-calendar-picker-indicator{display:none}input[type=number]::-webkit-inner-spin-button,input[type=number]::-webkit-outer-spin-button{-webkit-appearance:none;margin:0}
</style>
